import React, { FC, ReactElement, Fragment } from 'react';
import { useMutation } from '@apollo/client';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import DatePicker from 'components/@common/Inputs/DatePicker';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@material-ui/icons/Info';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import RemoveIcon from '@material-ui/icons/Remove';
import ContactsIcon from '@material-ui/icons/Contacts';
import SettingsCard from 'components/@common/Cards/SettingsCard';
import Popover from 'components/@common/Popover';
import Fab from 'components/@common/Fab';
import clsx from 'clsx';
import { Classroom, ScheduledActivityScheduleType } from 'types/schema-generated-types';
import useStore from 'store';
import getSettingSuccessAlert from '../../../utils/settings-success-alert';
import UPDATE_KEY_CLASSROOM_ASSESSMENTS_SETTING from 'graph/settings/key/update-key-classroom-assessments-setting-mutation';
import useKeySetting from 'components/ClassroomsModule/hooks/use-key-setting';
import { updateCache } from 'graph/update-cache';
import GET_KEYBOARDING_CLASSROOM_SETTINGS from 'graph/settings/key/get-keyboarding-classroom-settings-query';
import { formatDateString } from 'utilities/date-utils';
import startOfDay from 'date-fns/startOfDay';
interface KeyboardingAssessmentsInputs {
  label: string;
  value: ScheduledActivityScheduleType | 'NONE';
}

interface KeyboardingAssessmentProps {
  classroom: Classroom;
  expanded: string | null;
  setting: any;
  handleChangeExpanded: (panel: string | null) => () => void;
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(2)
    },
    label: {
      color: theme.palette.common.black,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing(1),
      marginLeft: theme.spacing(-1.3),
      '&.Mui-focused': {
        color: `${theme.palette.common.black} !important`
      }
    },
    labelNoMargin: {
      marginLeft: 0
    },
    datesContainerRemove: {
      marginLeft: theme.spacing(2),
      marginTop: theme.spacing(0.8),
      padding: `${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
      height: theme.typography.pxToRem(33),
      fontSize: theme.typography.pxToRem(16),
      lineHeight: theme.typography.pxToRem(21),
      width: theme.typography.pxToRem(33),
      minWidth: theme.typography.pxToRem(33)
    },
    datesContainer: {
      display: 'flex',
      alignItems: 'center'
    }
  };
});

const assessmentsOptions: KeyboardingAssessmentsInputs[] = [
  {
    label: "Don't schedule Assessments",
    value: 'NONE'
  },
  {
    label: 'Run assessments at beginning, middle and end of the year.',
    value: ScheduledActivityScheduleType.CURRICULUM
  },
  {
    label: 'Schedule assessments on specific dates.',
    value: ScheduledActivityScheduleType.CALENDAR
  }
];

const getClassroomKeySettingsMutationCacheUpdate = (classroomId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_KEYBOARDING_CLASSROOM_SETTINGS,
      variables: {
        where: {
          classroomId
        }
      },
      reducer: (
        cachedData,
        { updateKeyClassroomSettingAssessments }
      ): { keyClassroomSettings: any } => {
        return {
          keyClassroomSettings: {
            ...cachedData?.keyClassroomSettings,
            assessmentSettings: updateKeyClassroomSettingAssessments
          }
        };
      }
    }
  ])
});

const KeyboardingAssessment: FC<KeyboardingAssessmentProps> = ({
  setting: settingValue,
  classroom,
  expanded,
  handleChangeExpanded
}) => {
  const { id: classroomId } = classroom;
  const { t } = useTranslation('AssessmentSetting');
  const classes = useStyles();
  const [
    state,
    {
      onAddDate,
      onDateChange,
      onDeleteDate,
      onSelectionChange,
      refreshState,
      onToggleAssessOnLogin
    }
  ] = useKeySetting(settingValue);
  const [, { openAlert }] = useStore();
  const [updateKeyClassroomSettingAssessments] = useMutation(
    UPDATE_KEY_CLASSROOM_ASSESSMENTS_SETTING,
    getClassroomKeySettingsMutationCacheUpdate(+classroomId)
  );

  const handleOnCancel = (): void => {
    refreshState(settingValue);
  };

  const handleOnConfirm = async (): Promise<void> => {
    try {
      const scheduleDates = state.scheduledDates.map(d => startOfDay(new Date(d)));

      const result = await updateKeyClassroomSettingAssessments({
        variables: {
          data: {
            classroomId,
            scheduleType: state.scheduleType === 'NONE' ? null : state.scheduleType,
            assessOnLoginDate: state.assessOnLoginDate,
            scheduledDates:
              state.scheduleType === ScheduledActivityScheduleType.CALENDAR ? scheduleDates : []
          }
        }
      });
      refreshState(result.data.updateKeyClassroomSettingAssessments);
      openAlert(getSettingSuccessAlert('Assessments', t));
    } catch (error) {
      // TODO
      console.log('ERROR');
    }
  };

  return (
    <SettingsCard
      title={t('Teacher-Scheduled Assessments')}
      panel="keybordingAssessments"
      expanded={expanded}
      handleChangeExpanded={handleChangeExpanded}
      description={t('Check student speed and accuracy at different intervals during the year.')}
      onCancel={handleOnCancel}
      onSubmit={handleOnConfirm}
    >
      <Fragment>
        <FormControl component="fieldset" className={classes.container}>
          <FormLabel component="label" className={classes.label}>
            {t('Scheduled Assessments')}
          </FormLabel>
          <RadioGroup
            aria-label="keyboarding assessment setting"
            name="keybordingAssessment"
            value={state.scheduleType}
            onChange={onSelectionChange}
          >
            {assessmentsOptions.map(
              ({ label, value }, key): ReactElement => (
                <FormControlLabel
                  key={`language-option-${key}`}
                  value={value}
                  control={<Radio />}
                  label={label}
                />
              )
            )}
          </RadioGroup>
        </FormControl>
        {state.scheduleType === ScheduledActivityScheduleType.CALENDAR && (
          <Typography component="div">
            <Typography variant="caption" component="p">
              <InfoIcon />
              &nbsp;
              {t('You can schedule up to 5 assessments')}
            </Typography>
            <div>
              {state.scheduledDates.map((x, idx: number) => (
                <div key={`${idx}-date-picker-custom-date`} className={classes.datesContainer}>
                  <DatePicker
                    id={`date-picker-assessment-inline-${idx}`}
                    KeyboardButtonProps={{
                      'aria-label': t('change date assessments')
                    }}
                    inputProps={{
                      'aria-label': t('change date assessments')
                    }}
                    onChange={(newDate: Date | null): void => onDateChange(newDate, idx)}
                    value={x}
                  />
                  {state.scheduledDates.length > 1 && (
                    <Fab
                      variant="outlined"
                      color="secondary"
                      size="small"
                      className={classes.datesContainerRemove}
                      startIcon={<RemoveIcon />}
                      onClick={onDeleteDate(idx)}
                    >
                      <span />
                    </Fab>
                  )}
                </div>
              ))}
            </div>
            {state.scheduledDates.length < 5 && (
              <Typography component="p">
                <Button
                  variant="text"
                  size="small"
                  color="primary"
                  disableElevation
                  onClick={onAddDate}
                  startIcon={<AddCircleIcon />}
                >
                  {t('Add Another Custom Date')}
                </Button>
              </Typography>
            )}
          </Typography>
        )}
        <Typography component="div">
          <Popover
            title={t('On Demand Assessments')}
            description={t('This feature can be used in addition to your scheduled assessments.')}
          >
            <Typography
              variant="body1"
              component="p"
              className={clsx(classes.label, classes.labelNoMargin)}
            >
              {t('On Demand Assessments')}
            </Typography>
          </Popover>
          {!state.assessOnLoginDate && (
            <Typography variant="subtitle1" component="p">
              {t('Assess all students the next time they log in.')}
            </Typography>
          )}
          {state.assessOnLoginDate && (
            <Typography variant="subtitle1" component="p">
              <InfoIcon />
              &nbsp;
              {`${t('On Demand Assessments set on')}: ${formatDateString(
                new Date(state.assessOnLoginDate)
              )}`}
            </Typography>
          )}
          <Button
            variant="contained"
            color="secondary"
            size="small"
            disableElevation
            onClick={onToggleAssessOnLogin}
            startIcon={<ContactsIcon />}
          >
            {state.assessOnLoginDate
              ? t('Cancel On Demand Assessments')
              : t('Assess on next login')}
          </Button>
        </Typography>
      </Fragment>
    </SettingsCard>
  );
};

export default KeyboardingAssessment;
